import { useModal } from '@billy/design-system'
import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { MINIMAL_INVOICE_NUMBER } from '@features/invoices/constants/minimalInvoiceNumber'
import { getInvoiceDocumentTitle } from '@features/invoices/utils/getInvoiceDocumentTitle'
import { getInvoiceNumberDisplayedValue } from '@features/invoices/utils/getInvoiceNumberDisplayedValue'

import { ModalId } from '../../../../../../../../../../../enums/modalId'
import { invoiceEditorVariantToBillingStage } from '../../../../../../../constants/variants/invoiceEditorVariantToBillingStage'
import { useInvoiceEditorForm } from '../../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from '../../../../../../../hooks/useInvoiceEditorDocumentType'
import { useInvoiceEditorVariant } from '../../../../../../../hooks/useInvoiceEditorVariant'

const INVOICE_NUMBER_PREFIX_SEPARATOR = '-'

interface ContextState {
  closeInvoicePrefixAndNumberModal: () => void
  getDocumentDisplayedTitle: (prefix?: string | null) => string
  documentDisplayedTitle: string
  registeredInvoicePrefix: string | null
  saveInvoicePrefixAndNumber: () => void
}

const InvoicePrefixAndNumberContext = createContext<ContextState | undefined>(undefined)

interface InvoicePrefixAndNumberContextProps {
  children: ReactNode
}

export const InvoicePrefixAndNumberContextProvider = ({
  children,
}: InvoicePrefixAndNumberContextProps): ReactElement => {
  const { t } = useTranslation()
  const hasModalNumberingValuesBeenSaved = useRef(false)
  const hasInitialFormValuesBeenLoadedRef = useRef(false)
  const { documentType } = useInvoiceEditorDocumentType()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()
  const { invoiceEditorForm } = useInvoiceEditorForm()

  const [registeredInvoiceNumber, setRegisteredNumber] = useState(
    invoiceEditorForm.getValues('invoiceNumber') ?? MINIMAL_INVOICE_NUMBER,
  )
  const [registeredInvoicePrefix, setRegisteredPrefix] = useState<string | null>(
    invoiceEditorForm.getValues('invoiceNumberPrefix') ?? null,
  )

  const { close: closeInvoicePrefixAndNumberModal } = useModal(ModalId.InvoiceEditorInvoicePrefixAndNumberModal, {
    onClose: () => {
      // When modal is being closed in any other way than "Save" button click,
      // form values will be reverted to the initial - "registered" ones.
      if (!hasModalNumberingValuesBeenSaved.current) {
        setValue('invoiceNumber', registeredInvoiceNumber)
        setValue('invoiceNumberPrefix', registeredInvoicePrefix)
      }
      clearErrors(['invoiceNumber', 'invoiceNumberPrefix'])
      hasModalNumberingValuesBeenSaved.current = false
    },
  })

  const { clearErrors, watch, setValue, trigger } = invoiceEditorForm
  const [invoiceNumberFormValue, invoiceNumberPrefixFormValue, invoiceLanguage] = watch([
    'invoiceNumber',
    'invoiceNumberPrefix',
    'language',
  ])

  const documentTitle = useMemo(
    () =>
      getInvoiceDocumentTitle({
        billingStage: invoiceEditorVariantToBillingStage[invoiceEditorVariant],
        documentType,
        language: invoiceLanguage,
        t,
      }),
    [documentType, invoiceEditorVariant, invoiceLanguage, t],
  )

  // separated from documentDisplayedTitle to make it easier to present live title previews
  const getDocumentDisplayedTitle = useCallback(
    (prefix?: string | null) => {
      const registeredInvoicePrefixAndNumberDisplayValue = getInvoiceNumberDisplayedValue(
        invoiceNumberFormValue,
        prefix,
      )
      return `${documentTitle}${INVOICE_NUMBER_PREFIX_SEPARATOR}${registeredInvoicePrefixAndNumberDisplayValue}`
    },
    [documentTitle, invoiceNumberFormValue],
  )

  const documentDisplayedTitle = useMemo(
    () => getDocumentDisplayedTitle(registeredInvoicePrefix),
    [getDocumentDisplayedTitle, registeredInvoicePrefix],
  )

  const saveInvoicePrefixAndNumber = useCallback(async () => {
    const isFormValid = await trigger(['invoiceNumber', 'invoiceNumberPrefix'])

    if (isFormValid && invoiceNumberPrefixFormValue !== undefined && invoiceNumberFormValue !== undefined) {
      hasModalNumberingValuesBeenSaved.current = true
      // On "Save", we need to align form values with the registered values,
      // which are displayed in the Invoice Header.
      setRegisteredNumber(invoiceNumberFormValue)
      setRegisteredPrefix(invoiceNumberPrefixFormValue)
      closeInvoicePrefixAndNumberModal()
    }
  }, [closeInvoicePrefixAndNumberModal, invoiceNumberFormValue, invoiceNumberPrefixFormValue, trigger])

  useEffect(() => {
    const shouldRegisterInitialFormValues =
      !hasInitialFormValuesBeenLoadedRef.current &&
      invoiceNumberPrefixFormValue !== undefined &&
      invoiceNumberFormValue !== undefined

    if (shouldRegisterInitialFormValues) {
      setRegisteredPrefix(invoiceNumberPrefixFormValue)
      setRegisteredNumber(invoiceNumberFormValue)
      hasInitialFormValuesBeenLoadedRef.current = true
    }
  }, [invoiceNumberFormValue, invoiceNumberPrefixFormValue])

  const contextValue = useMemo(
    () => ({
      closeInvoicePrefixAndNumberModal,
      documentDisplayedTitle,
      getDocumentDisplayedTitle,
      registeredInvoicePrefix,
      saveInvoicePrefixAndNumber,
    }),
    [
      closeInvoicePrefixAndNumberModal,
      documentDisplayedTitle,
      getDocumentDisplayedTitle,
      registeredInvoicePrefix,
      saveInvoicePrefixAndNumber,
    ],
  )

  return (
    <InvoicePrefixAndNumberContext.Provider value={contextValue}>{children}</InvoicePrefixAndNumberContext.Provider>
  )
}

export const useInvoicePrefixAndNumber = () => {
  const context = useContext(InvoicePrefixAndNumberContext)

  if (!context) {
    throw new Error('InvoicePrefixAndNumberContextProvider is missing in the module!')
  }

  return context
}
